// src/components/Hero.jsx

import { useGSAP } from '@gsap/react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { useEffect, useState } from 'react';
import { TiLocationArrow } from 'react-icons/ti';
import { heroVideo, smallHeroVideo } from '../utils';
import { BackgroundBeams } from './ui/BackgroundBeams';
import { FlipWords } from './ui/FlipWords';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

const Hero = () => {
  const [videoSrc, setVideoSrc] = useState(
    window.innerWidth < 760 ? smallHeroVideo : heroVideo
  );

  const flipWords = ["Revolutionary", "Decentralised", "Secure", "Innovative", "Future-Ready"];

  const handleVideoSrcSet = () => {
    if (window.innerWidth < 760) {
      setVideoSrc(smallHeroVideo);
    } else {
      setVideoSrc(heroVideo);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleVideoSrcSet);

    return () => {
      window.removeEventListener('resize', handleVideoSrcSet);
    };
  }, []);

  useGSAP(() => {
    gsap.to('#hero', { opacity: 1, delay: 1.5 });
    gsap.to('#cta', { opacity: 1, y: -50, delay: 2 });
  }, []);

  const handleScrollToModelView = () => {
    const modelViewSection = document.getElementById('modelViewSection');
    if (modelViewSection) {
      modelViewSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="w-full bg-black relative h-screen flex flex-col justify-between overflow-hidden">
      {/* Animated Background Effects */}
      <BackgroundBeams className="z-0" />
      
      {/* Sparkles overlay */}
      <div className="absolute inset-0 z-[1]">
        <SparklesCore
          id="hero-sparkles"
          background="transparent"
          minSize={0.4}
          maxSize={1.4}
          particleDensity={50}
          particleColor="#22d3ee"
        />
      </div>

      {/* Top Content */}
      <div className="flex flex-col items-center pt-12 space-y-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-center"
        >
          <p id="hero" className="text-cyan-400 font-bold tracking-[0.4em] uppercase text-xs opacity-0">
            Welcome to Zero-Bank & Zero-Blockchain.xyz ICO
          </p>
          <div className="flex items-center justify-center mt-2">
            <span className="text-xl md:text-2xl text-gray-300">
              The{" "}
            </span>
            <FlipWords 
              words={flipWords} 
              className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent ml-2"
              duration={2500}
            />
            <span className="text-xl md:text-2xl text-gray-300 ml-2">
              Blockchain Platform
            </span>
          </div>
          
          {/* ZC-1 Protocol Badge */}
          <div className="flex items-center gap-3 mt-4">
            <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              PoS
            </span>
            <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
              BFT
            </span>
            <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-purple-500/10 border border-purple-500/30 text-purple-400">
              DAG
            </span>
            <span className="text-gray-400 text-xs">|</span>
            <span className="text-gray-400 text-xs font-medium">Powered by ZC-1 Hybrid Protocol</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="md:w-10/12 w-9/12 mb-6 max-h-[37.5rem] overflow-hidden rounded-[4rem] border border-white/10 shadow-2xl shadow-[0_0_80px_rgba(34,211,238,0.15)]"
        >
          <video
            className="pointer-events-none w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline={true}
            key={videoSrc}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        </motion.div>
      </div>

      {/* CTA Section */}
      <motion.div
        id="cta"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2 }}
        className="flex flex-col items-center mt-[18px] mb-20 relative z-10"
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <GlassButton
            onClick={handleScrollToModelView}
            ariaLabel="Navigate to Coin and Tokens Section"
            variant="cyan"
            size="lg"
            className="px-8"
          >
            <span className="flex items-center font-bold tracking-wide">
              Coin & Tokens
              <TiLocationArrow className="ml-2 text-xl" />
            </span>
          </GlassButton>
        </motion.div>

        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
          className="mt-6 text-center text-gray-300"
        >
          Our Native Zero-Coin a single payment of <strong className="text-cyan-400">0.34 ETH</strong> or Own Our Zero-Token starting at just <strong className="text-cyan-400">0.0034 ETH</strong>.
        </motion.p>
      </motion.div>
    </section>
  );
};

export default Hero;
